import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';

// Column definitions organized by section
export interface ColumnDef {
  key: string;
  label: string;
  fixed?: boolean; // Fixed columns cannot be hidden
  section: string;
}

export const ALL_COLUMNS: ColumnDef[] = [
  // Fixed columns (always shown)
  { key: 'id',     label: 'Location ID', fixed: true, section: 'Primary Information' },
  { key: 'sap',    label: 'SAP ID',      fixed: true, section: 'Primary Information' },
  { key: 'name',   label: 'Location Name', fixed: true, section: 'Primary Information' },
  { key: 'status', label: 'Status',      fixed: true, section: 'Primary Information' },

  // Facility classification
  { key: 'facilityType', label: 'Location Type', section: 'Facility Classification' },
  { key: 'type',         label: 'Department',    section: 'Facility Classification' },
  { key: 'org',          label: 'Organization Type', section: 'Facility Classification' },
  { key: 'tpl',          label: '3PL Status',    section: 'Facility Classification' },
  { key: 'size',         label: 'Location Size', section: 'Facility Classification' },
  { key: 'ownership',    label: 'Ownership',     section: 'Facility Classification' },
  { key: 'yearBuilt',    label: 'Year Built',    section: 'Facility Classification' },

  // Address & geography
  { key: 'address',  label: 'Street Address', section: 'Address & Geography' },
  { key: 'city',     label: 'City',           section: 'Address & Geography' },
  { key: 'state',    label: 'State / Province', section: 'Address & Geography' },
  { key: 'zip',      label: 'ZIP / Postal Code', section: 'Address & Geography' },
  { key: 'latitude', label: 'Latitude',       section: 'Address & Geography' },
  { key: 'longitude', label: 'Longitude',      section: 'Address & Geography' },
  { key: 'region',   label: 'Region',         section: 'Address & Geography' },
  { key: 'market',   label: 'Market',         section: 'Address & Geography' },

  // Resource assignment
  { key: 'marketLead', label: 'Market Lead',       section: 'Resource Assignment' },
  { key: 'regionLead', label: 'Region Lead',       section: 'Resource Assignment' },
  { key: 'coe',        label: 'Capability Resource', section: 'Resource Assignment' },
  { key: 'coe2',       label: 'E2E Resource',       section: 'Resource Assignment' },

  // Systems & labor
  { key: 'warehouseSystem', label: 'Warehouse System', section: 'Systems & Labor' },
  { key: 'labor',           label: 'Labor Model',       section: 'Systems & Labor' },
  { key: 'union',           label: 'Union Status',      section: 'Systems & Labor' },
  { key: 'supplier',        label: '3PL Supplier',      section: 'Systems & Labor' },

  // Capacity & scale
  { key: 'totalSqft',       label: 'Total Building Sqft', section: 'Capacity & Scale' },
  { key: 'operationalSqft',  label: 'Operational Sqft',    section: 'Capacity & Scale' },

  // Docks & production
  { key: 'docks',       label: 'Docks',                      section: 'Docks & Production' },
  { key: 'docksEng',    label: 'Docks with ENG Controls',    section: 'Docks & Production' },
  { key: 'docksNoEng',  label: 'Docks without ENG Controls', section: 'Docks & Production' },
  { key: 'driveIn',     label: 'Drive-in Bays',              section: 'Docks & Production' },
  { key: 'bottleLines', label: 'Bottle Lines',               section: 'Docks & Production' },
  { key: 'canLines',    label: 'Can Lines',                  section: 'Docks & Production' },

  // Network relationships
  { key: 'build1',   label: 'Crossdock Build Site 1', section: 'Network Relationships' },
  { key: 'build2',   label: 'Crossdock Build Site 2', section: 'Network Relationships' },
  { key: 'bulkSite', label: 'Centralized Bulk Site',  section: 'Network Relationships' },

  
  // Additional data
  { key: 'executionSystem', label: 'Execution System', section: 'Additional Data' },
  { key: 'warehouseStartTime', label: 'Warehouse Start Time', section: 'Additional Data' },
];

const SECTIONS = [
  'Primary Information',
  'Facility Classification',
  'Address & Geography',
  'Resource Assignment',
  'Systems & Labor',
  'Capacity & Scale',
  'Docks & Production',
  'Network Relationships',
  'Additional Data',
];

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <Svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <Path
      d={open ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'}
      stroke={AdminColors.blue900}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const ColumnsIcon = () => (
  <Svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 3H3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9M12 3h9a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-9M12 3v18"
      stroke={AdminColors.blue800}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Svg>
);

interface SectionProps {
  title: string;
  columns: ColumnDef[];
  visible: string[];
  onToggleColumn: (key: string) => void;
  persistKey: string;
}

const ColumnSection: React.FC<SectionProps> = ({
  title,
  columns,
  visible,
  onToggleColumn,
  persistKey,
}) => {
  const [expanded, setExpanded] = useState(true);

  const allSelected = columns.filter(c => !c.fixed).every(c => visible.includes(c.key));
  const someSelected = columns.some(c => visible.includes(c.key));

  const toggleAll = () => {
    const nonFixed = columns.filter(c => !c.fixed);
    if (allSelected) {
      // Deselect all non-fixed
      nonFixed.forEach(c => { if (visible.includes(c.key)) onToggleColumn(c.key); });
    } else {
      // Select all non-fixed
      nonFixed.forEach(c => { if (!visible.includes(c.key)) onToggleColumn(c.key); });
    }
  };

  return (
    <View style={sectionStyles.container}>
      {/* Section toggle header */}
      <TouchableOpacity
        style={sectionStyles.toggle}
        onPress={() => setExpanded(e => !e)}
        activeOpacity={0.7}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
          <ChevronIcon open={expanded} />
          <Text style={sectionStyles.toggleLabel}>{title}</Text>
        </View>
        {/* Select all for section */}
        {columns.some(c => !c.fixed) && (
          <TouchableOpacity
            onPress={(e) => { e.stopPropagation?.(); toggleAll(); }}
            activeOpacity={0.7}
            style={sectionStyles.masterBtn}
          >
            <Text style={sectionStyles.masterTxt}>{allSelected ? 'Deselect all' : 'Select all'}</Text>
          </TouchableOpacity>
        )}
      </TouchableOpacity>

      {/* Section body */}
      {expanded && (
        <View style={sectionStyles.body}>
          {columns.map(col => (
            <TouchableOpacity
              key={col.key}
              style={[sectionStyles.item, col.fixed && sectionStyles.itemFixed]}
              onPress={() => !col.fixed && onToggleColumn(col.key)}
              activeOpacity={col.fixed ? 1 : 0.7}
            >
              <View style={[
                sectionStyles.checkbox,
                visible.includes(col.key) && sectionStyles.checkboxChecked,
                col.fixed && sectionStyles.checkboxFixed,
              ]}>
                {visible.includes(col.key) && (
                  <Svg width="9" height="7" viewBox="0 0 10 8" fill="none">
                    <Path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                )}
              </View>
              <Text style={[sectionStyles.itemLabel, col.fixed && sectionStyles.itemLabelFixed]}>
                {col.label}
              </Text>
              {col.fixed && (
                <Text style={sectionStyles.fixedBadge}>FIXED</Text>
              )}
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

const sectionStyles = StyleSheet.create({
  container: {
    marginBottom: 7,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 7,
    overflow: 'hidden',
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    backgroundColor: AdminColors.blue25,
    paddingHorizontal: 9,
  },
  toggleLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  masterBtn: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  masterTxt: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  body: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 6,
    gap: 2,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 4,
    minWidth: '48%',
    flex: 0,
  },
  itemFixed: {
    opacity: 0.75,
  },
  itemLabel: {
    fontSize: 11,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  itemLabelFixed: {
    color: AdminColors.textMuted,
  },
  checkbox: {
    width: 15,
    height: 15,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },
  checkboxFixed: {
    borderColor: AdminColors.gray150,
  },
  fixedBadge: {
    fontSize: 9,
    fontWeight: '700',
    color: AdminColors.textMuted,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
});

// Props
interface ColumnPickerModalProps {
  visible: boolean;
  visibleColumns: string[];
  onClose: () => void;
  onApply: (columns: string[]) => void;
}

const ColumnPickerModal: React.FC<ColumnPickerModalProps> = ({
  visible,
  visibleColumns,
  onClose,
  onApply,
}) => {
  const [draft, setDraft] = useState<string[]>(visibleColumns);

  useEffect(() => {
    if (visible) setDraft(visibleColumns);
  }, [visible, visibleColumns]);

  const toggleColumn = (key: string) => {
    setDraft(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const selectAll = () => {
    setDraft(ALL_COLUMNS.map(c => c.key));
  };

  const selectFixed = () => {
    setDraft(ALL_COLUMNS.filter(c => c.fixed).map(c => c.key));
  };

  const visibleCount = draft.length;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={pickerStyles.backdrop}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={pickerStyles.container}>
        {/* Header */}
        <View style={pickerStyles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <ColumnsIcon />
            <View>
              <Text style={pickerStyles.title}>Columns</Text>
              <Text style={pickerStyles.subtitle}>
                {visibleCount} of {ALL_COLUMNS.length} columns shown
              </Text>
            </View>
          </View>
          <TouchableOpacity onPress={onClose} style={pickerStyles.closeBtn} activeOpacity={0.7}>
            <Text style={pickerStyles.closeTxt}>✕</Text>
          </TouchableOpacity>
        </View>

        {/* Master controls */}
        <View style={pickerStyles.masterRow}>
          <TouchableOpacity onPress={selectAll} activeOpacity={0.7}>
            <Text style={pickerStyles.masterLnk}>Select all</Text>
          </TouchableOpacity>
          <Text style={pickerStyles.masterSep}>·</Text>
          <TouchableOpacity onPress={selectFixed} activeOpacity={0.7}>
            <Text style={pickerStyles.masterLnk}>Fixed only</Text>
          </TouchableOpacity>
        </View>

        {/* Scrollable sections */}
        <ScrollView style={pickerStyles.scroll} showsVerticalScrollIndicator={false}>
          {SECTIONS.map(section => {
            const cols = ALL_COLUMNS.filter(c => c.section === section);
            if (!cols.length) return null;
            return (
              <ColumnSection
                key={section}
                title={section}
                columns={cols}
                visible={draft}
                onToggleColumn={toggleColumn}
                persistKey={section}
              />
            );
          })}
          <View style={{ height: 8 }} />
        </ScrollView>

        {/* Footer */}
        <View style={pickerStyles.footer}>
          <TouchableOpacity onPress={onClose} style={pickerStyles.cancelBtn} activeOpacity={0.7}>
            <Text style={pickerStyles.cancelTxt}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => { onApply(draft); onClose(); }}
            style={pickerStyles.applyBtn}
            activeOpacity={0.7}
          >
            <Text style={pickerStyles.applyTxt}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const pickerStyles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(18,35,55,0.2)',
  },
  container: {
    position: 'absolute',
    right: 28,
    top: 160,
    width: 440,
    maxHeight: '72%' as any,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    shadowColor: '#002E69',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 12,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  subtitle: {
    fontSize: 10,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginTop: 1,
  },
  closeBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: AdminColors.gray25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeTxt: {
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  masterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 4,
  },
  masterLnk: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  masterSep: {
    fontSize: 12,
    color: AdminColors.textMuted,
  },
  scroll: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: AdminColors.gray25,
  },
  cancelBtn: {
    height: 32,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  applyBtn: {
    height: 32,
    paddingHorizontal: 20,
    backgroundColor: AdminColors.blue800,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  applyTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
});

export default ColumnPickerModal;
